import { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import usePagination from '@/hooks/usePagination';
import {
  useGetAllClubBookingsQuery,
  useReturnClubResourceKeysMutation,
  useCancelClubBookingMutation,
} from '@/services/ClubBookingService';
import { useGetClubResourcesQuery } from '@/services/ClubResourceService';
import {
  ClubBookingStatus,
  ResourceType,
  ClubBooking,
  ClubBookingItem,
} from '@/types/club-booking-management.types';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { formatFinnishCurrency } from '@/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

export interface ClubResourceOption {
  value: string;
  label: string;
}

export interface ClubBookingsListingProps {
  clubId?: string;
}

const ClubBookingsListing = (props: ClubBookingsListingProps) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { clubId } = props;
  const { enqueueSnackbar } = useSnackbar();

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    action: 'return_keys' | 'cancel' | null;
    reason?: string;
  }>({ isOpen: false, bookingId: null, action: null });

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [resourceOptions, setResourceOptions] = useState<ClubResourceOption[]>(
    []
  );
  const [selectedResource, setSelectedResource] = useState<string>('');

  const { data: resourcesData, isLoading: isResourcesLoading } =
    useGetClubResourcesQuery({
      page: 1,
      limit: 100,
    });

  const extractErrorMessage = (err: unknown): string => {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object') {
      const obj = err as Record<string, unknown>;
      const data = obj['data'] as unknown;
      if (typeof data === 'string') return data;
      if (data && typeof data === 'object') {
        const d = data as Record<string, unknown>;
        if (typeof d['message'] === 'string') return d['message'] as string;
        if (typeof d['error'] === 'string') return d['error'] as string;
        if (d['error'] && typeof d['error'] === 'object') {
          const inner = d['error'] as Record<string, unknown>;
          if (typeof inner['message'] === 'string')
            return inner['message'] as string;
        }
      }
      if (typeof obj['error'] === 'string') return obj['error'] as string;
      if (typeof obj['message'] === 'string') return obj['message'] as string;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return 'Failed to update booking';
    }
  };

  const getStatusEnum = (filter: string): number | undefined => {
    const statusMap: { [key: string]: number } = {
      pending: ClubBookingStatus.PENDING, // 1
      confirmed: ClubBookingStatus.CONFIRMED, // 2
      cancelled: ClubBookingStatus.CANCELLED, // 3
      completed: ClubBookingStatus.COMPLETED, // 4
      noshow: ClubBookingStatus.PENDING_CONFIRMATION, // 5
    };
    return statusMap[filter];
  };

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    status: getStatusEnum(statusFilter),
    club_id: clubId || undefined,
    resource_id: selectedResource || undefined,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAllClubBookingsQuery(queryParams);

  const [returnClubResourceKeys] = useReturnClubResourceKeysMutation();
  const [cancelClubBooking] = useCancelClubBookingMutation();

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.total || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (resourcesData?.data) {
      const options = resourcesData.data.map((resource) => ({
        value: resource.id || '',
        label: resource.name,
      }));
      setResourceOptions(options);
    }
  }, [resourcesData]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [statusFilter, debouncedValue, selectedResource, pagination, refetch]);

  const filteredData = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return [];

    return (data.data as ClubBooking[]).map((booking) => ({
      id: booking.id,
      user: {
        name: booking.user.name,
        email: booking.user.email,
      },
      club: {
        name: booking.club.name,
        logo: booking.club.logo,
      },
      itemsCount: booking.items.length,
      totalPrice: booking.total_price,
      status: booking.status,
      paymentStatus: booking.payment_transactions[0]?.status || 'UNKNOWN',
      createdAt: booking.created_at,
      // Get the earliest start time and latest end time from all items
      startDate: booking.items.reduce(
        (earliest: string, item: ClubBookingItem) =>
          !earliest || new Date(item.start_time) < new Date(earliest)
            ? item.start_time
            : earliest,
        ''
      ),
      endDate: booking.items.reduce(
        (latest: string, item: ClubBookingItem) =>
          !latest || new Date(item.end_time) > new Date(latest)
            ? item.end_time
            : latest,
        ''
      ),
      // Get unique resource types
      resourceTypes: [
        ...new Set(
          booking.items.map(
            (item: ClubBookingItem) => item.resource.resource_type
          )
        ),
      ],
      // Check if any item has keys
      hasKeys: booking.items.some(
        (item: ClubBookingItem) =>
          item.resource.resource_type === ResourceType.KEYS
      ),
      // Check if any confirmed item has keys (for return keys button)
      hasConfirmedKeys: booking.items.some(
        (item: ClubBookingItem) =>
          item.resource.resource_type === ResourceType.KEYS &&
          item.status === ClubBookingStatus.CONFIRMED
      ),
    }));
  }, [data]);

  const handleReturnKeys = (bookingId: string) => {
    setConfirmationDialog({
      isOpen: true,
      bookingId: bookingId,
      action: 'return_keys',
    });
  };

  const handleCancelBooking = (bookingId: string) => {
    setConfirmationDialog({
      isOpen: true,
      bookingId: bookingId,
      action: 'cancel',
    });
  };

  const handleEditClick = useCallback(
    (bookingId: string) => {
      navigate(`/club-booking-management/${bookingId}/edit`);
    },
    [navigate]
  );

  const handleViewClick = useCallback(
    (bookingId: string) => {
      if (clubId) {
        // If we're in a club context, pass that information
        navigate(
          `/club-booking-management/${bookingId}?from=club&clubId=${clubId}`,
          {
            state: { from: 'club-details' },
          }
        );
      } else {
        navigate(`/club-booking-management/${bookingId}`);
      }
    },
    [navigate, clubId]
  );

  const handleConfirmAction = async () => {
    if (!confirmationDialog.bookingId || !confirmationDialog.action) return;

    try {
      if (confirmationDialog.action === 'return_keys') {
        // Find the booking and get all key items
        if (!data?.data || !Array.isArray(data.data)) {
          console.error('No bookings data available');
          return;
        }
        const bookings = data.data as ClubBooking[];
        const booking = bookings.find(
          (b: ClubBooking) => b.id === confirmationDialog.bookingId
        );

        if (!booking) {
          console.error('Booking not found for return keys action');
          return;
        }

        // Get all key items that are confirmed
        const keyItems = booking.items.filter(
          (item) =>
            item.resource.resource_type === ResourceType.KEYS &&
            item.status === ClubBookingStatus.CONFIRMED
        );

        if (keyItems.length === 0) {
          console.error('No confirmed key items found');
          return;
        }

        // Call the return keys API with all key items
        await returnClubResourceKeys({
          items: keyItems.map((item) => ({
            item_id: item.id,
            quantity: item.quantity || 1,
          })),
        }).unwrap();
      } else if (confirmationDialog.action === 'cancel') {
        await cancelClubBooking({
          id: confirmationDialog.bookingId,
          data: {
            cancellation_reason:
              confirmationDialog.reason || 'Cancelled by admin',
          },
        }).unwrap();
      }

      setConfirmationDialog({ isOpen: false, bookingId: null, action: null });
      refetch();
    } catch (error) {
      console.error('Error updating booking:', error);
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setConfirmationDialog((prev) => ({ ...prev, reason: e.target.value }));
  };

  const getResourceTypeLabel = (type: ResourceType): string => {
    switch (type) {
      case ResourceType.SAUNA_BOOKING:
        return 'Club Premises';
      case ResourceType.KEYS:
        return 'Keys';
      case ResourceType.ANNUAL_INSPECTIONS:
        return 'Annual Inspections';
      case ResourceType.PORT_WATCH_SHIFTS:
        return 'Port Watch Shifts';
      default:
        return 'Unknown';
    }
  };

  const getBookingStatusLabel = (status: ClubBookingStatus): string => {
    switch (status) {
      case ClubBookingStatus.CONFIRMED:
        return 'Confirmed';
      case ClubBookingStatus.CANCELLED:
        return 'Cancelled';
      case ClubBookingStatus.COMPLETED:
        return 'Completed';
      case ClubBookingStatus.PENDING_CONFIRMATION:
        return 'Pending Confirmation';
      default:
        return 'Payment Pending';
    }
  };

  const getBookingStatusBadgeClass = (status: ClubBookingStatus): string => {
    switch (status) {
      case ClubBookingStatus.CONFIRMED:
        return 'bg-green-100 text-green-800';
      case ClubBookingStatus.CANCELLED:
        return 'bg-red-100 text-red-800';
      case ClubBookingStatus.COMPLETED:
        return 'bg-blue-100 text-blue-800';
      case ClubBookingStatus.PENDING_CONFIRMATION:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getPaymentStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'SUCCEEDED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'user',
        header: 'User',
        accessorKey: 'user',
        enableSorting: true,
        cell: ({
          row,
        }: {
          row: { original: { user: { name: string | null; email: string } } };
        }) => (
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">
              {row.original.user.name || 'N/A'}
            </span>
            <span className="text-sm text-gray-500">
              {row.original.user.email}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: { club: { name: string } }) => row.club.name,
        id: 'club_name',
        header: 'Club',
        enableSorting: false,
        cell: ({ row }: { row: { original: { club: { name: string } } } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.club.name}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: { itemsCount: number }) => row.itemsCount,
        id: 'items_count',
        header: 'Items',
        enableSorting: false,
        cell: ({
          row,
        }: {
          row: {
            original: { itemsCount: number; resourceTypes: ResourceType[] };
          };
        }) => (
          <div className="flex flex-col">
            <span className="text-gray-800 font-normal">
              {row.original.itemsCount} item
              {row.original.itemsCount !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-gray-500">
              {row.original.resourceTypes
                .map((type) => getResourceTypeLabel(type))
                .join(', ')}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: { startDate: string }) => row.startDate,
        id: 'start_date',
        header: 'Start Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: { startDate: string } } }) => (
          <div className="flex flex-col">
            <span className="text-gray-800 font-normal">
              {formatDisplayDate(new Date(
                row.original.startDate.split('T')[0]
              ))}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(row.original.startDate).toLocaleTimeString('en-US', {
                timeZone: 'UTC',
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: { endDate: string }) => row.endDate,
        id: 'end_date',
        header: 'End Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: { endDate: string } } }) => (
          <div className="flex flex-col">
            <span className="text-gray-800 font-normal">
              {formatDisplayDate(new Date(
                row.original.endDate.split('T')[0]
              ))}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(row.original.endDate).toLocaleTimeString('en-US', {
                timeZone: 'UTC',
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: { totalPrice: number }) => row.totalPrice,
        id: 'total_price',
        header: 'Total Price',
        enableSorting: false,
        cell: ({ row }: { row: { original: { totalPrice: number } } }) => (
          <span className="text-gray-800 font-normal">
            {formatFinnishCurrency(row.original.totalPrice)}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: { status: ClubBookingStatus }) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({
          row,
        }: {
          row: { original: { id: string; status: ClubBookingStatus } };
        }) => (
          <span
            data-testid={`status-${row.original.id}`}
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBookingStatusBadgeClass(
              row.original.status
            )}`}
          >
            {getBookingStatusLabel(row.original.status)}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: { paymentStatus: string }) => row.paymentStatus,
        id: 'payment_status',
        header: 'Payment',
        enableSorting: false,
        cell: ({ row }: { row: { original: { paymentStatus: string } } }) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusBadgeClass(
              row.original.paymentStatus
            )}`}
          >
            {row.original.paymentStatus}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: { createdAt: string }) => row.createdAt,
        id: 'created_at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }: { row: { original: { createdAt: string } } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.createdAt))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({
          row,
        }: {
          row: {
            original: {
              id: string;
              user: { name: string | null };
              hasConfirmedKeys: boolean;
              status: ClubBookingStatus;
            };
          };
        }) => (
          <div className="flex items-center gap-2">
            {hasPermission('club_resource_booking_management_read') && (
              <button
                type="button"
                onClick={() => handleViewClick(row.original.id)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View"
                aria-label={`View ${row.original.user.name}'s booking`}
                disabled={isLoading || isFetching}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {(row.original.status === ClubBookingStatus.PENDING ||
              row.original.status === ClubBookingStatus.PENDING_CONFIRMATION) &&
              hasPermission('club_resource_booking_management_update') && (
                <button
                  type="button"
                  onClick={() => handleEditClick(row.original.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  title="Edit"
                  aria-label={`Edit ${row.original.user.name}'s booking`}
                  disabled={isLoading || isFetching}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}
            {row.original.hasConfirmedKeys && (
              <button
                type="button"
                className="btn btn-sm btn-light-success"
                onClick={() => handleReturnKeys(row.original.id)}
                title="Return Keys"
                aria-label={`Return keys for ${row.original.user.name}'s booking`}
              >
                <KeenIcon type="filled" icon="key" className="fs-4" />
                Return Keys
              </button>
            )}
            {hasPermission('club_resource_booking_management_cancel') && (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light btn-outline"
                onClick={() => handleCancelBooking(row.original.id)}
                title="Cancel"
                aria-label={`Cancel ${row.original.user.name}'s booking`}
                disabled={
                  row.original.status === ClubBookingStatus.CANCELLED ||
                  row.original.status === ClubBookingStatus.COMPLETED
                }
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-red-500"
                />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[220px]' },
      },
    ],
    [handleEditClick, handleViewClick, hasPermission, isLoading, isFetching]
  );

  const resetFilters = () => {
    resetSearch();
    setStatusFilter('all');
    setSelectedResource('');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <div className="club-bookings-listings card card-grid w-full">
      <div className="card-header club-bookings-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="club-bookings-header-left flex items-center gap-2">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-club-bookings"
              aria-label="Search club bookings by user or club"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-club-bookings"
                placeholder="Search bookings by user or club"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search club bookings by user, resource, or date"
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter bookings by status"
              disabled={isLoading || isFetching}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
              <option value="noshow">Pending Confirmation</option>
            </select>
          </div>
          <div className="flex-shrink-0 w-full sm:w-64">
            <Select
              id="resource-filter"
              options={resourceOptions}
              value={
                resourceOptions.find(
                  (option) => option.value === selectedResource
                ) || null
              }
              onChange={(selectedOption) => {
                const resourceId = selectedOption ? selectedOption.value : '';
                setSelectedResource(resourceId);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              isDisabled={isLoading || isFetching || isResourcesLoading}
              placeholder={
                isResourcesLoading ? 'Loading resources...' : 'Select resource'
              }
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: '0.5rem',
                  minHeight: '2.5rem',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  boxShadow: 'none',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 50,
                  marginTop: '0.5rem',
                  borderRadius: '0.375rem',
                }),
                option: (base) => ({
                  ...base,
                  fontSize: '0.875rem',
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                }),
              }}
              aria-label="Filter bookings by resource"
            />
          </div>
        </div>
        <div className="club-bookings-header-right flex items-center gap-2">
          {(searchTerm && searchTerm.trim() !== '') ||
          statusFilter !== 'all' ||
          selectedResource ? (
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          ) : null}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <>
            {!isLoading && !isError && filteredData.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">No club bookings available</p>
              </div>
            )}
            {!isLoading && !isError && filteredData.length > 0 && (
              <div className="club-bookings-grid-wrapper">
                <DataGrid
                  data-testid="club-bookings-data-grid"
                  columns={columns}
                  data={filteredData}
                  rowSelect={false}
                  loading={isLoading || isFetching}
                  pagination={{
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    totalItems: data?.total || totalItems,
                    totalPages: data?.totalPages || totalPages,
                    setPagination,
                    info: `{from} - {to} of {count}`,
                    sizes: [5, 10, 25, 50, 100],
                    sizesLabel: 'Show',
                    sizesDescription: 'per page',
                  }}
                  sorting={[{ id: 'user_name', desc: false }]}
                  serverSide={true}
                  hasActiveFilters={
                    !!searchTerm || statusFilter !== 'all' || !!selectedResource
                  }
                  aria-label="Club bookings table"
                />
              </div>
            )}
          </>
        )}
        <ConfirmationDialog
          open={confirmationDialog.isOpen}
          onClose={() =>
            setConfirmationDialog({
              isOpen: false,
              bookingId: null,
              action: null,
              reason: undefined,
            })
          }
          onConfirm={handleConfirmAction}
          message={
            confirmationDialog.action === 'return_keys' ? (
              <p>Are you sure you want to mark these keys as returned?</p>
            ) : (
              <>
                <p>Are you sure you want to cancel this booking?</p>
                <div className="mb-3">
                  <label className="form-label">Cancellation Reason</label>
                  <textarea
                    className="form-control bg-light-active w-full textarea mt-3"
                    rows={3}
                    value={confirmationDialog.reason || ''}
                    onChange={handleReasonChange}
                    placeholder="Enter reason for cancellation..."
                  />
                </div>
              </>
            )
          }
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="action-confirmation-title"
          aria-describedby="action-confirmation-description"
        />
      </div>
    </div>
  );
};

export default ClubBookingsListing;
